import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowRightLeft, Plus, Trash2, Search } from 'lucide-react';
import { useCreateTransfer, type CreateTransferInput } from '../../../api/mutations/transfer.mutations';
import { useBranches, useWarehouse } from '../../../api/queries/transfer.queries';
import { useIngredients } from '../../../api/queries/inventory.queries';
import { useAuthStore } from '../../../stores/auth.store';

interface CreateTransferModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface TransferItem {
  ingredientId: string;
  ingredientName: string;
  requestedQuantity: number;
  unit: string;
  notes?: string;
}

export default function CreateTransferModal({ onClose, onSuccess }: CreateTransferModalProps) {
  const { t } = useTranslation();
  const { user: _user } = useAuthStore();

  const [type, setType] = useState<CreateTransferInput['type']>('warehouse_to_branch');
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<TransferItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showIngredientSearch, setShowIngredientSearch] = useState(false);

  const { data: branches } = useBranches();
  const { data: warehouse } = useWarehouse();
  const { data: ingredients } = useIngredients();
  const createTransfer = useCreateTransfer();

  // Filter branches based on type
  const sourceBranches = branches?.filter(b => {
    if (type === 'warehouse_to_branch') return b.isWarehouse;
    if (type === 'branch_to_warehouse') return !b.isWarehouse;
    return !b.isWarehouse; // branch_to_branch
  }) || [];

  const destBranches = branches?.filter(b => {
    if (type === 'warehouse_to_branch') return !b.isWarehouse;
    if (type === 'branch_to_warehouse') return b.isWarehouse;
    return !b.isWarehouse && b._id !== fromLocationId; // branch_to_branch
  }) || [];

  // Auto-set warehouse for warehouse_to_branch and branch_to_warehouse
  useState(() => {
    if (warehouse && type === 'warehouse_to_branch') {
      setFromLocationId(warehouse._id);
    } else if (warehouse && type === 'branch_to_warehouse') {
      setToLocationId(warehouse._id);
    }
  });

  const filteredIngredients = ingredients?.filter(ing =>
    ing.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ing.sku.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const addItem = (ingredient: { _id: string; name: string; unit: string }) => {
    if (items.some(item => item.ingredientId === ingredient._id)) {
      // Already added
      return;
    }
    setItems([...items, {
      ingredientId: ingredient._id,
      ingredientName: ingredient.name,
      requestedQuantity: 1,
      unit: ingredient.unit,
    }]);
    setShowIngredientSearch(false);
    setSearchTerm('');
  };

  const updateItem = (index: number, updates: Partial<TransferItem>) => {
    setItems(items.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!toLocationId) {
      alert(t('transfers.selectDestination'));
      return;
    }

    if (items.length === 0) {
      alert(t('transfers.addAtLeastOneItem'));
      return;
    }

    try {
      await createTransfer.mutateAsync({
        type,
        fromLocationId: type === 'warehouse_to_branch' ? warehouse?._id : fromLocationId,
        toLocationId,
        items: items.map(item => ({
          ingredientId: item.ingredientId,
          requestedQuantity: item.requestedQuantity,
          unit: item.unit,
          notes: item.notes,
        })),
        notes: notes || undefined,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create transfer:', error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('transfers.createTransfer')}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Transfer Type */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('transfers.type')} *</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setType('warehouse_to_branch');
                    setFromLocationId(warehouse?._id || '');
                    setToLocationId('');
                  }}
                  className={`p-3 rounded-lg border text-sm ${
                    type === 'warehouse_to_branch'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {t('transfers.warehouseToBranch')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setType('branch_to_branch');
                    setFromLocationId('');
                    setToLocationId('');
                  }}
                  className={`p-3 rounded-lg border text-sm ${
                    type === 'branch_to_branch'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {t('transfers.branchToBranch')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setType('branch_to_warehouse');
                    setFromLocationId('');
                    setToLocationId(warehouse?._id || '');
                  }}
                  className={`p-3 rounded-lg border text-sm ${
                    type === 'branch_to_warehouse'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {t('transfers.branchToWarehouse')}
                </button>
              </div>
            </div>

            {/* Source & Destination */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('transfers.source')} *</label>
                {type === 'warehouse_to_branch' ? (
                  <input
                    type="text"
                    value={warehouse?.name || ''}
                    disabled
                    className="w-full rounded-lg border bg-secondary px-3 py-2 text-muted-foreground"
                  />
                ) : (
                  <select
                    value={fromLocationId}
                    onChange={(e) => setFromLocationId(e.target.value)}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  >
                    <option value="">{t('common.select')}</option>
                    {sourceBranches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('transfers.destination')} *</label>
                {type === 'branch_to_warehouse' ? (
                  <input
                    type="text"
                    value={warehouse?.name || ''}
                    disabled
                    className="w-full rounded-lg border bg-secondary px-3 py-2 text-muted-foreground"
                  />
                ) : (
                  <select
                    value={toLocationId}
                    onChange={(e) => setToLocationId(e.target.value)}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  >
                    <option value="">{t('common.select')}</option>
                    {destBranches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Expected Delivery Date */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('transfers.expectedDelivery')}</label>
              <input
                type="date"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                className="w-full max-w-xs rounded-lg border bg-background px-3 py-2"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">{t('transfers.items')} *</label>
                <button
                  type="button"
                  onClick={() => setShowIngredientSearch(true)}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {t('transfers.addItem')}
                </button>
              </div>

              {/* Ingredient Search */}
              {showIngredientSearch && (
                <div className="mb-4 p-4 border rounded-lg bg-secondary/50">
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('transfers.searchIngredients')}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border bg-background"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {filteredIngredients.slice(0, 10).map((ing) => (
                      <button
                        key={ing._id}
                        type="button"
                        onClick={() => addItem(ing)}
                        disabled={items.some(item => item.ingredientId === ing._id)}
                        className="w-full text-left p-2 rounded hover:bg-secondary flex justify-between items-center disabled:opacity-50"
                      >
                        <span>{ing.name}</span>
                        <span className="text-xs text-muted-foreground">{ing.sku} • {ing.unit}</span>
                      </button>
                    ))}
                    {filteredIngredients.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {t('transfers.noIngredientsFound')}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowIngredientSearch(false);
                      setSearchTerm('');
                    }}
                    className="mt-2 text-sm text-muted-foreground hover:underline"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('transfers.ingredient')}</th>
                      <th className="text-right py-2 px-3 w-32">{t('transfers.quantity')}</th>
                      <th className="text-left py-2 px-3 w-20">{t('transfers.unit')}</th>
                      <th className="text-left py-2 px-3">{t('common.notes')}</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.ingredientId} className="border-t">
                        <td className="py-2 px-3">{item.ingredientName}</td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={item.requestedQuantity}
                            onChange={(e) => updateItem(index, { requestedQuantity: parseFloat(e.target.value) || 0 })}
                            className="w-full rounded border bg-background px-2 py-1 text-right"
                            min={0.1}
                            step={0.1}
                            required
                          />
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{item.unit}</td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={item.notes || ''}
                            onChange={(e) => updateItem(index, { notes: e.target.value })}
                            className="w-full rounded border bg-background px-2 py-1"
                            placeholder={t('common.optional')}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-1 text-destructive hover:bg-destructive/10 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">{t('transfers.noItemsAdded')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
                placeholder={t('transfers.notesPlaceholder')}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createTransfer.isPending || items.length === 0}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {createTransfer.isPending ? t('common.creating') : t('transfers.createTransfer')}
          </button>
        </div>
      </div>
    </div>
  );
}
